import { Button } from '@app/components/various/Button';
import { CancelSmall } from '@app/components/various/Icons';
import { ShareButton } from '@app/components/various/ShareButton';
import { createAbsoluteUrl } from '@app/utils/createAbsoluteUrl';
import { useOffsetWidth } from '@app/utils/hooks/useOffsetWidth';
import { Product } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CSSTransition, SwitchTransition } from 'react-transition-group';

import { ProductPrice } from '../ProductPrice';

import { NavigationArrows } from './NavigationArrows';

interface Props {
  activeVariant: Product.Standard;
  productDetails: Product.Standard;
  onHideModal: () => void;
}

export const ProductDetailsModalHeader = ({ activeVariant, productDetails, onHideModal }: Props) => {
  const { t } = useTranslation(['products']);
  const ref = React.useRef<HTMLDivElement>(null);
  const width = useOffsetWidth(ref);
  const shareableUrl = createAbsoluteUrl('products', {
    showProduct: activeVariant.product,
    showVariant: activeVariant.variant,
  });

  return (
    <div className="sticky top-0 z-pdm bg-white px-4 pb-2 pt-5 shadow-4 shadow-white lg:px-8 lg:pt-8">
      <div className="flex justify-between gap-3">
        <SwitchTransition>
          <CSSTransition
            key={productDetails.product}
            timeout={150}
            classNames={{
              enter: 'opacity-0',
              enterActive: '!opacity-100 transition-opacity',
              exit: 'opacity-100',
              exitActive: '!opacity-0 transition-opacity',
            }}
          >
            <div ref={ref} className="overflow-hidden">
              <div className="flex items-center gap-4">
                <h2 className="overflow-hidden break-words heading-1 lg:mb-1 lg:text-[32px] lg:leading-10">
                  {activeVariant.name}
                  <ShareButton
                    tooltipText={t('products:copy_shareable_link')}
                    shareURL={shareableUrl}
                    size="large"
                    className="ml-2 mr-1 align-middle md:ml-3 md:align-top"
                    message={t('products:share_button_message')}
                    popoverOverflowContainer={false}
                  />
                </h2>
              </div>
              <ProductPrice productInfo={activeVariant} size="large" parentWidth={width} />
            </div>
          </CSSTransition>
        </SwitchTransition>
        <div className="hidden gap-5 sm:flex">
          <NavigationArrows />
          <Button icon={CancelSmall} aria-label="Close modal" color="secondary" variant="flat" onClick={onHideModal} />
        </div>
      </div>
    </div>
  );
};
